"use client";

import type {
  SelectContentProps,
  SelectProps as RadixSelectProps,
  SelectTriggerProps,
  SelectValueProps,
} from "@radix-ui/react-select";
import {
  Content,
  Portal,
  Root,
  Trigger,
  Value,
  Viewport,
} from "@radix-ui/react-select";
import { AnimatePresence, motion } from "motion/react";
import type { Ref } from "react";
import { createContext } from "react";

import { cn } from "../../utils";
import { Icon } from "../Icon";
import type { SelectVariant } from "./Select.types";

export const SelectContext = createContext<SelectVariant>("default");

export interface SelectProps<T extends string> extends Omit<
  RadixSelectProps,
  "value" | "defaultValue" | "onValueChange"
> {
  ref?: Ref<HTMLButtonElement>;
  value?: T;
  align?: SelectContentProps["align"];
  variant?: SelectVariant;
  tabIndex?: number;
  sideOffset?: SelectContentProps["sideOffset"];
  valueProps?: SelectValueProps;
  defaultValue?: T;
  triggerProps?: SelectTriggerProps;
  contentProps?: SelectContentProps;
  iconClassName?: string;
  valueComponent?: React.ReactNode;
  triggerElement?: React.ReactNode;
  triggerClassName?: string;
  contentClassName?: string;
  viewportClassName?: string;
  onValueChange?: (value: T) => void;
}

export const Select = <T extends string>({
  ref,
  align,
  variant = "default",
  children,
  tabIndex = 0,
  sideOffset,
  valueProps,
  triggerProps,
  contentProps,
  iconClassName,
  valueComponent,
  triggerElement,
  triggerClassName,
  contentClassName,
  viewportClassName,
  ...props
}: SelectProps<T>) => {
  const isDefaultVariant = variant === "default";
  const isToolbarVariant = variant === "toolbar";
  const isMiniVariant = variant === "mini";
  const isBuiltinVariant = variant === "builtin";

  return (
    <Root
      open={props.open}
      value={props.value}
      disabled={props.disabled}
      defaultOpen={props.defaultOpen}
      defaultValue={props.defaultValue}
      onOpenChange={props.onOpenChange}
      onValueChange={props.onValueChange}
    >
      {triggerElement ? (
        <Trigger
          ref={ref}
          tabIndex={tabIndex}
          {...triggerProps}
          className={cn(
            "ui:focus:outline-none",
            {
              "ui:cursor-pointer": !props.disabled,
            },
            triggerClassName,
          )}
        >
          {triggerElement}
        </Trigger>
      ) : (
        <Trigger
          ref={ref}
          tabIndex={tabIndex}
          {...triggerProps}
          className={cn(
            "ui:group ui:flex ui:w-full ui:items-center ui:justify-between ui:border ui:transition-colors ui:not-disabled:cursor-pointer ui:not-disabled:hover:bg-buttonHover ui:focus:outline-none ui:aria-expanded:bg-buttonHover",
            {
              "ui:h-11 ui:rounded-sm ui:border-divider ui:bg-white ui:px-3 ui:pr-4 ui:text-textPrimary ui:aria-expanded:rounded-b-none ui:dark:bg-buttonBg":
                isDefaultVariant,
              "ui:h-12 ui:rounded-none ui:border-y-0 ui:border-divider ui:bg-background ui:px-3 ui:pr-4 ui:dark:bg-buttonBg":
                isToolbarVariant,
              "ui:h-5 ui:rounded-sm ui:border-divider ui:bg-buttonBg ui:px-1 ui:text-xxs ui:text-textPrimary ui:aria-expanded:border-divider":
                isMiniVariant,
              "ui:relative ui:h-full ui:justify-around ui:border-transparent ui:bg-transparent ui:text-xs ui:enabled:hover:bg-transparent ui:aria-expanded:bg-transparent":
                isBuiltinVariant,
            },
            triggerClassName,
          )}
        >
          {valueComponent ?? <Value {...valueProps} />}
          {!props.disabled && (
            <Icon
              size="xs"
              name="caret-down"
              className={cn(
                "ui:transform-gpu ui:text-textSecondary ui:transition-transform ui:ease-out ui:will-change-transform ui:group-aria-expanded:rotate-180 ui:group-aria-expanded:text-textPrimary",
                {
                  "ui:text-[9px]": isMiniVariant,
                  "ui:absolute": isBuiltinVariant,
                },
                iconClassName,
              )}
            />
          )}
        </Trigger>
      )}

      <AnimatePresence>
        <Portal>
          <SelectContext.Provider value={variant}>
            <Content
              asChild
              align={align}
              position="popper"
              collisionPadding={0}
              data-testid="select-content"
              sideOffset={sideOffset ?? (isDefaultVariant ? -1 : 0)}
              {...contentProps}
            >
              <motion.div
                exit={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                initial={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className={cn(
                  "ui:w-(--radix-select-trigger-width) ui:animate-fade ui:overflow-hidden ui:rounded-sm ui:rounded-t-none ui:bg-buttonBg ui:shadow-[0px_2px_14px_0px_rgba(201,201,201,0.50)] ui:animate-duration-300 ui:dark:border ui:dark:border-divider ui:dark:shadow-none",
                  {
                    "ui:text-textPrimary": isDefaultVariant,
                    "ui:relative ui:top-px ui:left-px ui:w-[calc(var(--radix-popper-anchor-width)-1px)] ui:rounded-t-none ui:dark:top-0 ui:dark:left-0 ui:dark:w-[calc(var(--radix-popper-anchor-width)+1px)]":
                      isToolbarVariant,
                    "ui:rounded-none ui:text-textPrimary ui:dark:border-0":
                      isMiniVariant,
                    "ui:border ui:border-divider ui:shadow-none":
                      isBuiltinVariant,
                  },
                  contentClassName,
                )}
              >
                <Viewport>
                  <div className={cn("ui:overflow-auto", viewportClassName)}>
                    {children}
                  </div>
                </Viewport>
              </motion.div>
            </Content>
          </SelectContext.Provider>
        </Portal>
      </AnimatePresence>
    </Root>
  );
};
